import { Modal } from "../../components/ui/modal";
import { CheckCircle } from "lucide-react";
import { useEffect, useState } from "react";
import Input from "../../components/form/input/InputField";
import ListboxComponent from "../../components/common/ListboxComponent";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/common/Loader";
import { styles } from "../../styles/pages/Lead/LeadEditStyles";

// Interfaces
interface Stage {
  id: number;
  stage_name: string;
}

interface User {
  id: number;
  name: string;
}

interface LeadStatus {
  id: number;
  status_name: string;
}

interface LeadSource {
  id: number;
  source_name: string;
}

interface Errors {
  stage_id?: string;
  name?: string;
  address?: string;
  email?: string;
  contact?: string;
  source_id?: string;
  status_id?: string;
  opportunity_amount?: string;
}

interface FormData {
  id: string;
  stage_id: string;
  name: string;
  address: string;
  email: string;
  contact: string;
  source_id: string;
  status_id: string;
  company_name: string;
  company_website: string;
  opportunity_amount: string;
  referred_by: string;
  value: string;
  description: string;
}

const EditLead: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = sessionStorage.getItem("access_token");

  const [formData, setFormData] = useState<FormData>({
    id: "",
    stage_id: "",
    name: "",
    address: "",
    email: "",
    contact: "",
    source_id: "",
    status_id: "",
    company_name: "",
    company_website: "",
    opportunity_amount: "",
    referred_by: "",
    value: "",
    description: "",
  });

  const [stages, setStages] = useState<Stage[]>([]);
  const [, setUsers] = useState<User[]>([]);
  const [leadStatuses, setLeadStatuses] = useState<LeadStatus[]>([]);
  const [leadSources, setLeadSources] = useState<LeadSource[]>([]);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (id) {
      setLoading(true);
      fetch(`${process.env.REACT_APP_API_URL}/api/leads/fetch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data && data.lead) {
            setFormData({
              id: id,
              stage_id: data.lead.lead_stage.toString(),
              name: data.lead.name || "",
              address: data.lead.address || "",
              email: data.lead.email || "",
              contact: data.lead.contact || "",
              source_id: data.lead.lead_source.toString(),
              status_id: data.lead.lead_status.toString(),
              company_name: data.lead.company_name || "",
              company_website: data.lead.company_website || "",
              opportunity_amount: data.lead.opportunity_amount || "",
              referred_by: data.lead.referred_by || "",
              value: data.lead.value || "",
              description: data.lead.description || "",
            });
          }
        })
        .catch((error) => console.error("Error fetching lead data:", error))
        .finally(() => setLoading(false));
    }
  }, [id, token]);

  // Handle Input Change
  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({
      ...prev,
      [field]:
        field === "email"
          ? !isValidEmail(value)
            ? "Please enter a valid email address."
            : undefined
          : field === "contact"
            ? !validContact(value)
              ? "Please enter a valid 10-digit contact number."
              : undefined
            : value.trim() === ""
              ? `The ${field.replace(/_/g, " ")} field is required`
              : undefined,
    }));
  };

  // Handle Contact Number
  const handleContactNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, contact: formattedValue }));
    setErrors((prev) => ({
      ...prev,
      contact: !formattedValue
        ? "Mobile number field is required"
        : !validContact(formattedValue)
          ? "Please enter a valid 10-digit contact number."
          : undefined,
    }));
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validContact = (contact: string): boolean => {
    return /^\d{10}$/.test(contact);
  };

  // Validate Form
  const validate = (): Errors => {
    const newErrors: Errors = {};
    if (!formData.stage_id.trim()) newErrors.stage_id = "The stage field is required";
    if (!formData.name.trim()) newErrors.name = "The name field is required";
    if (!formData.address.trim()) newErrors.address = "The address field is required";
    if (!formData.email.trim()) newErrors.email = "The email field is required";
    if (!formData.contact.trim()) newErrors.contact = "The contact field is required";
    if (!formData.source_id.trim()) newErrors.source_id = "The lead source field is required";
    if (!formData.status_id.trim()) newErrors.status_id = "The lead status field is required";
    if (!formData.opportunity_amount.trim()) newErrors.opportunity_amount = "The opportunity amount field is required";
    return newErrors;
  };

  // Handle Form Submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setSaving(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leads/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: formData.id,
          lead_stage: formData.stage_id,
          name: formData.name,
          address: formData.address,
          email: formData.email,
          contact: formData.contact,
          lead_source: formData.source_id,
          lead_status: formData.status_id,
          company_name: formData.company_name,
          company_website: formData.company_website,
          opportunity_amount: formData.opportunity_amount,
          referred_by: formData.referred_by,
          value: formData.value,
          description: formData.description,
        }),
      });

      const data = await response.json();
      if (data.status === 200) {
        setSuccessModalOpen(true);
        setTimeout(() => {
          setSuccessModalOpen(false);
          navigate("/lead-list");
        }, 2000);
      } else {
        setErrors({ email: data.message || "Something went wrong." });
      }
    } catch (error: any) {
      setErrors({ email: error.message || "Failed to update lead. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  // Fetch Data
  useEffect(() => {
    const fetchStages = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/stage/name/list`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await response.json();
        if (result.status === 200) setStages(result.data);
      } catch (error) {
        console.error("Error fetching stages:", error);
      }
    };
    fetchStages();
  }, [token]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/name/list`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data.status === 200) setUsers(data.data);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [token]);

  useEffect(() => {
    const fetchLeadStatuses = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/status/name/list`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await response.json();
        if (result.status === 200) setLeadStatuses(result.data);
      } catch (error) {
        console.error("Error fetching lead statuses:", error);
      }
    };
    fetchLeadStatuses();
  }, [token]);

  useEffect(() => {
    const fetchLeadSources = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/source/name/list`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data.status === 200) setLeadSources(data.data);
      } catch (error) {
        console.error("Error fetching lead sources:", error);
      }
    };
    fetchLeadSources();
  }, [token]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Edit Lead</h2>
        {loading && <Loader />}
      </div>

      {!loading && (
        <div className={styles.formContainer}>
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.formFields}>
              {/* Name */}
              <div>
                <label className={styles.label}>
                  Name <span className={styles.required}>*</span>
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={`${styles.inputField} ${errors.name ? styles.inputError : styles.inputDefault}`}
                />
                {errors.name && <span className={styles.errorText}>{errors.name}</span>}
              </div>

              {/* Email */}
              <div>
                <label className={styles.label}>
                  Email <span className={styles.required}>*</span>
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className={`${styles.inputField} ${errors.email ? styles.inputError : styles.inputDefault}`}
                />
                {errors.email && <span className={styles.errorText}>{errors.email}</span>}
              </div>

              {/* Contact */}
              <div>
                <label className={styles.label}>
                  Mobile Number <span className={styles.required}>*</span>
                </label>
                <Input
                  type="tel"
                  value={formData.contact}
                  onChange={handleContactNumber}
                  className={`${styles.inputField} ${errors.contact ? styles.inputError : styles.inputDefault}`}
                />
                {errors.contact && <span className={styles.errorText}>{errors.contact}</span>}
              </div>

              {/* Lead Source */}
              <div>
                <ListboxComponent
                  value={leadSources.find((source) => source.id === Number(formData.source_id)) ?? null}
                  onChange={(source) => handleInputChange("source_id", source ? String(source.id) : "")}
                  items={leadSources}
                  label="Lead Source"
                  displayKey="source_name"
                  error={errors.source_id}
                  required
                />
              </div>

              {/* Lead Stage */}
              <div>
                <ListboxComponent
                  value={stages.find((stage) => stage.id === Number(formData.stage_id)) ?? null}
                  onChange={(stage) => handleInputChange("stage_id", stage ? String(stage.id) : "")}
                  items={stages}
                  label="Lead Stage"
                  displayKey="stage_name"
                  error={errors.stage_id}
                  required
                />
              </div>

              {/* Lead Status */}
              <div>
                <ListboxComponent
                  value={leadStatuses.find((status) => status.id === Number(formData.status_id)) ?? null}
                  onChange={(status) => handleInputChange("status_id", status ? String(status.id) : "")}
                  items={leadStatuses}
                  label="Lead Status"
                  displayKey="status_name"
                  error={errors.status_id}
                  required
                />
              </div>

              {/* Company Name */}
              <div>
                <label className={styles.label}>Company Name</label>
                <Input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange("company_name", e.target.value)}
                  className={`${styles.inputField} ${styles.inputDefault}`}
                />
              </div>

              {/* Company Website */}
              <div>
                <label className={styles.label}>Company Website</label>
                <Input
                  type="text"
                  value={formData.company_website}
                  onChange={(e) => handleInputChange("company_website", e.target.value)}
                  className={`${styles.inputField} ${styles.inputDefault}`}
                />
              </div>

              {/* Address */}
              <div>
                <label className={styles.label}>
                  Address <span className={styles.required}>*</span>
                </label>
                <Input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  className={`${styles.inputField} ${errors.address ? styles.inputError : styles.inputDefault}`}
                />
                {errors.address && <span className={styles.errorText}>{errors.address}</span>}
              </div>

              {/* Opportunity Amount */}
              <div>
                <label className={styles.label}>
                  Opportunity Amount <span className={styles.required}>*</span>
                </label>
                <Input
                  type="text"
                  value={formData.opportunity_amount}
                  onChange={(e) => handleInputChange("opportunity_amount", e.target.value)}
                  className={`${styles.inputField} ${errors.opportunity_amount ? styles.inputError : styles.inputDefault}`}
                  numericOnly={true}
                />
                {errors.opportunity_amount && (
                  <span className={styles.errorText}>{errors.opportunity_amount}</span>
                )}
              </div>

              {/* Referred By */}
              <div>
                <label className={styles.label}>Referred By</label>
                <Input
                  type="text"
                  value={formData.referred_by}
                  onChange={(e) => handleInputChange("referred_by", e.target.value)}
                  className={`${styles.inputField} ${styles.inputDefault}`}
                />
              </div>

              {/* Value */}
              <div>
                <label className={styles.label}>Value</label>
                <Input
                  type="text"
                  value={formData.value}
                  onChange={(e) => handleInputChange("value", e.target.value)}
                  className={`${styles.inputField} ${styles.inputDefault}`}
                  numericOnly={true}
                />
              </div>

              {/* Description */}
              <div className="col-span-1 md:col-span-2">
                <label className={styles.label}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  className={`${styles.inputField} min-h-[100px]`}
                />
              </div>
            </div>

            <div className={styles.buttonsContainer}>
              <button
                className={styles.submitButton}
                type="submit"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                className={styles.cancelButton}
                type="button"
                onClick={() => navigate("/lead-list")}
              >
                Cancel
              </button>
            </div>

            <Modal
              isOpen={isSuccessModalOpen}
              onClose={() => setSuccessModalOpen(false)}
              className={styles.modal}
            >
              <button
                onClick={() => setSuccessModalOpen(false)}
                className={styles.modalCloseButton}
              ></button>
              <div className={styles.modalIconContainer}>
                <CheckCircle className="w-6 h-6" />
              </div>
              <h4 className={styles.modalTitle}>Lead Updated Successfully</h4>
              <p className={styles.modalMessage}>
                The lead has been updated in the system successfully.
              </p>
              <div className={styles.modalButtonContainer}>
                <button
                  onClick={() => setSuccessModalOpen(false)}
                  className={styles.modalButton}
                >
                  OK
                </button>
              </div>
            </Modal>
          </form>
        </div>
      )}
    </div>
  );
};

export default EditLead;
